import React from 'react';
import CreatableSelect from 'react-select/creatable';
import { MdDelete } from "react-icons/md";
import CustomSelect from '../AddMedicines/CustomSelect';
import { IoCopyOutline } from "react-icons/io5";

const TestParameterRow = ({
    item,
    parentIndex,
    subIndex = null,
    handleTestFieldChange,
    handleNestedRefChange,
    addNestedRefRange,
    removeNestedRefRange,
    removeTestRow,
    handleAddSubTest,
    specificTestOptions,
    valueOptions,
    optionsList,
    error,
    tests
}) => {

    const isSubTest = subIndex !== null;
    const isMaxExceeded = item.order && parseInt(item.order) > 10;
    const isDuplicateOrder = React.useMemo(() => {
        if (!item.order || item.order === '') return false;

        let siblings = isSubTest
            ? (tests[parentIndex]?.sub_tests || [])
            : (tests || []);

        let currentIndex = isSubTest ? subIndex : parentIndex;

        return siblings.some((sibling, idx) =>
            idx !== currentIndex &&
            sibling.order?.toString() === item.order?.toString()
        );
    }, [item.order, tests, parentIndex, subIndex, isSubTest]);
    const hasOrderError = isDuplicateOrder || isMaxExceeded;
    const onFieldChange = (field, value) => {
        handleTestFieldChange(parentIndex, field, value, subIndex);
    };

    const onRefChange = (refIndex, field, value) => {
        handleNestedRefChange(parentIndex, refIndex, field, value, subIndex);
    };

    const onAddRef = () => {
        addNestedRefRange(parentIndex, subIndex);
    };

    const onRemoveRef = (refIndex) => {
        removeNestedRefRange(parentIndex, refIndex, subIndex);
    };

    const onRemoveRow = () => {
        removeTestRow(parentIndex, subIndex);
    };

    return (
        <div style={{
            position: 'relative',
            marginBottom: isSubTest ? '15px' : '0',
            backgroundColor: isSubTest ? '#ffffff' : 'transparent',
            padding: isSubTest ? '15px' : '0',
            borderRadius: isSubTest ? '8px' : '0',
            border: isSubTest ? '1px solid #eee' : 'none'
        }}>
            {/* Delete Button */}
            <span
                style={{
                    position: 'absolute', top: isSubTest ? '10px' : '-5px', right: '0px', margin: 0,
                    cursor: 'pointer', fontWeight: '600', fontSize: '18px', color: '#dc3545',
                    zIndex: 10, lineHeight: '1', fontFamily: 'sans-serif'
                }}
                onClick={onRemoveRow}
                title="Remove Parameter"
            >
                <MdDelete />
            </span>

            <div className='pricing-inputcontainer' style={{ marginBottom: '10px' }}>
                <span>{isSubTest ? 'Subparameter Name' : 'Parameter Name'}</span>
                <CreatableSelect
                    styles={{
                        control: (provided) => ({
                            ...provided, width: '100%', minHeight: '44px',
                            border: error ? '1px solid red' : '1px solid #E3E3E3',
                            boxShadow: 'none',
                            '&:hover': { border: error ? '1px solid red' : '1px solid #E3E3E3' },
                        }),
                        menu: (provided) => ({ ...provided, zIndex: 9999 }),
                        menuList: (provided) => ({ ...provided, maxHeight: '200px', zIndex: 9999 }),
                    }}
                    options={specificTestOptions}
                    value={item.selectedOption}
                    onChange={(option) => onFieldChange('selectedOption', option)}
                    placeholder={isSubTest ? "Select Subparameter" : "Select Parameter"}
                    isClearable={true}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <div className='pricing-inputcontainer' style={{ flex: 1, margin: 0 }}>
                    <span>Unit</span>
                    <input
                        name="unit"
                        placeholder='Unit'
                        value={item.unit}
                        onChange={(e) => onFieldChange('unit', e.target.value)}
                    />
                </div>

                <div className='pricing-inputcontainer' style={{ flex: 1, margin: 0 }}>
                    <span>Order</span>
                    <input
                        type="number"
                        name="order"
                        placeholder='0-10'
                        min="0" max="10"
                        value={item.order}
                        onChange={(e) => onFieldChange('order', e.target.value)}
                        style={{
                            border: hasOrderError ? '1px solid red' : '1px solid #E3E3E3'
                        }}
                    />
                    {isMaxExceeded ? (
                        <span style={{ color: 'red', fontSize: '11px', marginTop: '2px', display: 'block' }}>
                            Max value is 10
                        </span>
                    ) : isDuplicateOrder ? (
                        <span style={{ color: 'red', fontSize: '11px', marginTop: '2px', display: 'block' }}>
                            Duplicate Order
                        </span>
                    ) : null}
                </div>

                <div className='pricing-inputcontainer' style={{ flex: 1, margin: 0 }}>
                    <span>Value</span>
                    <CustomSelect
                        options={valueOptions}
                        value={valueOptions.find(opt => opt.value === item.value) || null}
                        onChange={(selectedOption) => onFieldChange('value', selectedOption ? selectedOption.value : '')}
                        placeholder="Select Value"
                    />
                </div>
            </div>

            <div className='pricing-inputcontainer'>
                <span>Reference Range</span>
                {(item.reference_parameter || []).map((range, refIndex) => (
                    <div key={refIndex} style={{ display: 'flex', gap: '5px', marginBottom: '10px', alignItems: 'center' }}>
                        <div className='col-5' style={{ padding: 0 }}>
                            <CustomSelect
                                options={optionsList}
                                placeholder='Category'
                                value={optionsList?.find(opt => opt.value === range.category) || null}
                                onChange={(opt) => onRefChange(refIndex, 'category', opt ? opt.value : '')}
                            />
                        </div>
                        <input
                            className='col-3' placeholder='Min' value={range.min_value}
                            onChange={(e) => onRefChange(refIndex, 'min_value', e.target.value)}
                        />
                        <input
                            className='col-3' placeholder='Max' value={range.max_value}
                            onChange={(e) => onRefChange(refIndex, 'max_value', e.target.value)}
                        />
                        {refIndex > 0 && (
                            <button
                                type="button"
                                title="Copy from above"
                                style={{ cursor: 'pointer', background: 'none', border: 'none', borderRadius: '4px' }}
                                onClick={() => {
                                    const prevRange = item.reference_parameter[refIndex - 1];
                                    onRefChange(refIndex, 'min_value', prevRange.min_value);
                                    onRefChange(refIndex, 'max_value', prevRange.max_value);
                                    // onRefChange(refIndex, 'category', prevRange.category);
                                }}
                            >
                                <IoCopyOutline />

                            </button>
                        )}
                        <button
                            type="button" className='remove-btn'
                            onClick={() => onRemoveRef(refIndex)}
                        > - </button>
                    </div>
                ))}
                <button
                    type="button" className='add-btn' style={{ width: '100%', marginTop: '5px' }}
                    onClick={onAddRef}
                > + Add Range </button>
            </div>

            {/* <div className='pricing-inputcontainer'>
                <span>Note</span>
                <input
                    name="note" placeholder='Note' value={item.note}
                    onChange={(e) => onFieldChange('note', e.target.value)}
                />
            </div> */}


        </div>
    );
};

export default TestParameterRow;